import { CloudUploadOutlined } from '@ant-design/icons';
import {
  Badge,
  Button,
  Drawer,
  message,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
} from 'antd';
import axios from 'axios';
import { PAGE_SIZE_OPTIONS } from 'constants/table';
import { usePagination } from 'hooks/usePagination';
import { useEffect, useState } from 'react';
import { formatDate } from 'utils';
import { downloadFile } from 'utils/files';
import { thousandFormatter } from 'utils/forms/formatter';

const PAGE_TITLE = 'Загрузки';

function UploadHistoryDrawer({ open, onClose, uploadUrl, zipUpload = false }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const {
    page,
    perPage,
    totalRows,
    setPagination,
    setTotalRows,
    // resetPagination,
  } = usePagination();

  const getHistory = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/org/v1/history_uploads/', {
        params: {
          page,
          size: perPage,
        },
      });
      setHistory(res.data.items);
      setTotalRows(res.data.total);
    } catch (error) {
      message.error(
        error.response?.data?.detail || 'Ошибка при получении истории загрузок'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!page || !perPage || !open) {
      return;
    }
    getHistory();
  }, [page, perPage, open]);

  const handleUpload = async (file, isZip = false) => {
    const hide = message.loading('Загрузка файла...', 0);

    const formData = new FormData();
    formData.append(isZip ? 'zip_file' : 'file', file);

    try {
      const url = isZip
        ? 'org/v1/clients/digital_signature/import/'
        : uploadUrl;

      const response = await axios.post(url, formData, {
        headers: {
          Accept: 'application/json',
        },
      });

      if (response.status === 201) {
        Modal.success({
          title: 'Успешно',
          content: 'Файл успешно загружен. Начинается обработка данных.',
          okText: 'ОК',
          onOk: () => {
            getHistory();
          },
        });
      }
    } catch (error) {
      if (error.response && error.response.status === 400) {
        Modal.error({
          title: 'Ошибка валидации',
          content:
            error.response.data.detail ||
            'Пожалуйста, проверьте файл и попробуйте снова.',
          okText: 'ОК',
        });
      } else {
        Modal.error({
          title: 'Ошибка загрузки',
          content:
            error.response?.data?.detail ||
            'Произошла ошибка при загрузке файла. Пожалуйста, попробуйте позже.',
          okText: 'ОК',
        });
      }
    } finally {
      hide();
    }
  };

  // const handleDelete = async (id) => {
  //   try {
  //     await axios.delete(`/org/v1/history_uploads/${id}/`);
  //     message.success('Запись успешно удалена');
  //     getHistory();
  //   } catch (error) {
  //     message.error(
  //       error.response?.data?.detail || 'Ошибка при удалении записи'
  //     );
  //   }
  // };

  const colors = {
    CREATED: 'default',
    PROCESSING: 'processing',
    INGESTION: 'processing',
    COMPLETED: 'success',
    COMPLETED_WITH_ERRORS: 'warning',
    FAILED: 'error',
  };

  const text = {
    CREATED: 'Создан',
    PROCESSING: <div style={{ fontStyle: 'italic' }}>В обработке...</div>,
    INGESTION: <div style={{ fontStyle: 'italic' }}>Загружается...</div>,
    COMPLETED: 'Завершено',
    COMPLETED_WITH_ERRORS: 'Завершено с ошибками',
    FAILED: 'Ошибка',
  };

  const columns = [
    {
      title: 'Название файла',
      dataIndex: 'origin_filename',
      key: 'origin_filename',
      render: (val, record) => (
        <Button
          style={{ padding: 0 }}
          type="link"
          onClick={() => downloadFile(record.imported_from, val)}
        >
          {val?.length > 30 ? `${val?.slice(0, 30)}...` : val}
        </Button>
      ),
      width: '250px',
      ellipsis: true,
      fixed: 'left',
    },
    {
      title: 'Дата',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (val) => formatDate(val, 'DD.MM.YYYY'),
      width: '100px',
    },
    // {
    //   title: 'Дата завершения',
    //   dataIndex: 'created_end',
    //   key: 'created_end',
    //   render: (val) => (val ? formatDate(val, 'DD.MM.YYYY HH:mm') : '- - -'),
    // },
    {
      title: 'Раздел',
      dataIndex: 'type',
      key: 'type',
      render: (val) => {
        switch (val) {
          case 'Импорт клиентов':
            return 'Клиенты';
          case 'products':
            return 'Товары';
          case 'turns':
            return 'Обращения';
          case 'sales':
            return 'Продажи';
          default:
            return val;
        }
      },
    },
    {
      title: 'Пользователь',
      dataIndex: 'user_name',
      key: 'user_name',
      render: (val) => (
        <Typography.Text style={{ maxWidth: '120px' }} ellipsis>
          {val}
        </Typography.Text>
      ),
      width: '120px',
    },
    {
      title: 'Всего',
      dataIndex: 'total_count_rows',
      key: 'total_count_rows',
      render: thousandFormatter,
    },

    {
      title: 'Успешно',
      dataIndex: 'success_rows',
      key: 'success_rows',
      render: (val) => (val ? thousandFormatter(val) : '- - -'),
    },
    {
      title: 'Ошибка',
      dataIndex: 'error_rows',
      key: 'error_rows',
      render: (val) => (val ? thousandFormatter(val) : '- - -'),
    },
    {
      title: 'Результат',
      dataIndex: 'imported_to',
      key: 'imported_to',
      render: (val, record) =>
        val ? (
          <Button
            style={{ padding: 0 }}
            type="link"
            onClick={() =>
              downloadFile(
                record.imported_to,
                record.type === 'Определение статуса ИП клиентов'
                  ? 'RESULT'
                  : 'ERROR'
              )
            }
          >
            {record.type === 'Определение статуса ИП клиентов'
              ? 'RESULT'
              : 'ERROR'}
          </Button>
        ) : (
          '- - -'
        ),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (val) => (
        <Tag color={colors[val] ?? 'default'}>{text[val] ?? val}</Tag>
      ),
    },

    // {
    //   key: 'action',
    //   render: (text, record) => (
    //     <Popconfirm
    //       title="Вы уверены, что хотите удалить этот элемент?"
    //       onConfirm={() => handleDelete(record.id)}
    //     >
    //       <Button icon={<DeleteOutlined />} />
    //     </Popconfirm>
    //   ),
    // },
  ];

  useEffect(() => {
    let intervalId;

    if (open && history.find((item) => item.status === 'В Обработке')) {
      intervalId = setInterval(() => {
        getHistory();
      }, 15000);
    } else if (intervalId) {
      clearInterval(intervalId);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [history, open]);

  return (
    <Drawer
      title={
        <>
          {PAGE_TITLE} <Badge offset={[-2, -10]} count={totalRows} />
        </>
      }
      width="70%"
      extra={
        <Space>
          <Typography.Text
            style={{ color: 'rgba(0, 0, 0, 0.45)', cursor: 'default' }}
          >
            Для импорта воспользуйтесь{' '}
            <button
              // onClick={handleDownloadExel}
              onClick={() => {
                downloadFile('/template', 'Шаблон.xlsx', true);
              }}
              style={{
                textDecoration: 'underline',
                background: 'none',
                border: 'none',
                color: 'rgba(0, 0, 0, 0.45)',
              }}
            >
              шаблоном
            </button>
          </Typography.Text>

          {zipUpload && (
            <Upload
              multiple={false}
              showUploadList={false}
              name="file"
              maxCount={1}
              accept=".zip"
              beforeUpload={() => false}
              onChange={(info) => {
                const { file } = info;
                if (file) {
                  handleUpload(file, true);
                }
              }}
            >
              <Button icon={<CloudUploadOutlined />}>Загрузить ЭЦП</Button>
            </Upload>
          )}

          <Upload
            multiple={false}
            showUploadList={false}
            name="file"
            maxCount={1}
            accept=".xlsx"
            beforeUpload={() => false}
            onChange={(info) => {
              const { file } = info;
              if (file) {
                handleUpload(file);
              }
            }}
          >
            <Button icon={<CloudUploadOutlined />}>Загрузить файл</Button>
          </Upload>
        </Space>
      }
      open={open}
      onClose={onClose}
    >
      <Table
        columns={columns}
        dataSource={history}
        pagination={{
          pageSize: perPage,
          total: totalRows,
          showSizeChanger: true,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
        }}
        scroll={{ x: 1440 }}
        loading={loading}
        onChange={setPagination}
        // onRow={(record) => {
        //   return {
        //     onDoubleClick: () => onRowClick(record),
        //   };
        // }}
      />
    </Drawer>
  );
}

export default UploadHistoryDrawer;
